import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Save, Users } from 'lucide-react';
import { Input } from '@/presentation/components/atoms/Input';
import { Button } from '@/presentation/components/atoms/Button';
import { useServices } from '@/app/providers/ServiceContext';
import { useToast, getErrorMessage } from '@/app/providers';

interface HikCentralVisitorGroupConfigProps {
  integrationId: string;
}

export const HikCentralVisitorGroupConfig: React.FC<HikCentralVisitorGroupConfigProps> = ({ integrationId }) => {
  const { integrationCatalogRepo } = useServices();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialValue, setInitialValue] = useState<string>('');
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await integrationCatalogRepo.getProviderConfig('HIKCENTRAL');
        if (cancelled) return;
        const current = (cfg.config?.visitorGroupName as string | undefined) ?? '';
        setInitialValue(current);
        setValue(current);
      } catch (e) {
        if (cancelled) return;
        setError(getErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [integrationCatalogRepo]);

  const dirty = value.trim() !== initialValue.trim();

  const handleSave = async () => {
    setSaving(true);
    try {
      const trimmed = value.trim();
      await integrationCatalogRepo.updateConfig(integrationId, { visitorGroupName: trimmed });
      setInitialValue(trimmed);
      setValue(trimmed);
      toast.success('Nombre del Visitor Group actualizado.');
    } catch (e) {
      toast.error(getErrorMessage(e), 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} className="text-primary-500" />
        <h4 className="text-sm font-semibold text-slate-800">Visitor Group (HikCentral)</h4>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Loader2 size={18} className="animate-spin text-slate-400" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && (
        <>
          <Input
            label="Nombre del Visitor Group"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Visitors"
            disabled={saving}
          />
          <p className="text-xs text-slate-400 mt-2">
            Debe coincidir con el grupo en HikCentral (Visitor Management → Visitor Group).
            Vacío = default <code className="font-mono">"Visitors"</code>.
          </p>
          <div className="flex justify-end mt-3">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!dirty}
              isLoading={saving}
            >
              <span className="flex items-center gap-1.5">
                <Save size={14} />
                Guardar
              </span>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
